import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of confetti pieces, default 24 */
  count?: number;
  /** Array of fill colours, default festive palette */
  colors?: string[];
  /** Base fall duration seconds, default 4 */
  dur?: number;
}

/**
 * Confetti — falling, rotating, swaying coloured rectangles.
 * Perfect for celebration, promotion, or party-themed banners.
 */
const Confetti: React.FC<Props> = ({
  count = 24,
  colors = ["#ff4d6d", "#ffd166", "#06d6a0", "#4cc9f0", "#c77dff"],
  dur = 4,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      size: 5 + r(1) * 4,
      delay: r(2) * dur,
      d: dur * (0.7 + r(3) * 0.7),
      color: colors[i % colors.length],
      spin: 360 + r(4) * 360,
      sway: 15 + r(5) * 20,
    }),
    [count, colors.join(","), dur]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-confetti-fall {
          0%   { transform: translateY(-10px); opacity: 0; }
          8%   { opacity: 1; }
          100% { transform: translateY(105vh); opacity: 0.9; }
        }
        @keyframes oot-confetti-sway {
          0%, 100% { margin-left: calc(var(--sway) * -1); }
          50%       { margin-left: var(--sway); }
        }
        @keyframes oot-confetti-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(var(--spin)); }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: 0,
            animation: `oot-confetti-fall ${p.d}s linear ${p.delay}s infinite`,
          }}
        >
          <div
            style={{
              animation: `oot-confetti-sway ${p.d / 2}s ease-in-out infinite`,
              "--sway": `${p.sway}px`,
            } as React.CSSProperties}
          >
            <div
              style={{
                width: p.size,
                height: p.size * 0.5,
                background: p.color,
                animation: `oot-confetti-spin ${p.d / 1.5}s linear infinite`,
                "--spin": `${p.spin}deg`,
              } as React.CSSProperties}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default Confetti;
